import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
    getSupportQuerys,
    UpdateComplaint,
    addMessage,
    updateSeen,
} from "../../../../Service/help";
import { getPartner } from "../../../../Service/partnerAPI";
import Loader from "../../../../reusableUI/loader";
import { FiSend } from "react-icons/fi";
import { MdCircle } from "react-icons/md";

const COMPLAINT_OPTIONS = [
    { value: "app_issue", label: "Portal Issue" },
    { value: "payment", label: "Payment" },
    { value: "billing", label: "Billing" },
    { value: "service", label: "Service Related" },
    { value: "account", label: "Account" },
    { value: "feedback", label: "Feedback" },
    { value: "other", label: "Other" },
];

const getInitials = (text) => {
    if (!text) return "?";
    const words = text.trim().split(" ");
    if (!words.length) return "?";
    if (words.length === 1) return words[0][0].toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
};

const getUnseenAdminReplies = (responses = []) =>
    responses.filter((r) => r.who === "admin" && !r.seen).length;

const sortTicketsByLatestActivity = (tickets = []) =>
    [...tickets].sort((a, b) => {
        const latestA =
            a.responses && a.responses.length > 0
                ? new Date(
                    a.responses.reduce(
                        (latest, res) =>
                            new Date(res.dateTime) > new Date(latest)
                                ? res.dateTime
                                : latest,
                        a.responses[0].dateTime
                    )
                )
                : new Date(a.updatedAt);

        const latestB =
            b.responses && b.responses.length > 0
                ? new Date(
                    b.responses.reduce(
                        (latest, res) =>
                            new Date(res.dateTime) > new Date(latest)
                                ? res.dateTime
                                : latest,
                        b.responses[0].dateTime
                    )
                )
                : new Date(b.updatedAt);

        return latestB - latestA;
    });

const HelpAdmin = () => {
    const [loading, setLoading] = useState(true);
    const [tickets, setTickets] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);

    const [responses, setResponses] = useState([]);
    const [supportID, setSupportID] = useState(null);
    const [status, setStatus] = useState(null);

    const [chatInput, setChatInput] = useState("");
    const [complaintType, setComplaintType] = useState("");
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [isSeenStatus, setIsSeenStatus] = useState(false);
    const [userName, setUserName] = useState(null);

    // scroll the inner chat area, not the whole page
    const messagesContainerRef = useRef(null);

    useEffect(() => {
        fetchTickets();
        getUser();
    }, []);

    // Restore selected ticket after tickets are loaded
    useEffect(() => {
        if (tickets.length > 0 && !selectedTicket && !isCreatingNew) {
            const savedTicketId = sessionStorage.getItem("selectedTicketId");
            if (savedTicketId) {
                const foundTicket = tickets.find((t) => t._id === savedTicketId);
                if (foundTicket) {
                    handleSelectTicket(foundTicket);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tickets]);

    // Only scroll chat container, and only when there is an active thread
    useEffect(() => {
        if (!selectedTicket && !isCreatingNew) return;
        if (!messagesContainerRef.current) return;

        const container = messagesContainerRef.current;
        container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth",
        });
    }, [responses, selectedTicket, isCreatingNew]);

    useEffect(() => {
        if (responses.length > 0 && isSeenStatus && supportID) {
            markAdminRepliesSeen();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSeenStatus]);

    const getUser = async () => {
        try {
            const res = await getPartner();
            if (res.error === false) {
                setUserName(res?.data?.basicInfo?.name || null);
            }
        } catch (error) {
            console.error("Error fetching user", error);
        }
    };

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const res = await getSupportQuerys();
            console.log(res);
            if (res.error === false && Array.isArray(res.data)) {
                setTickets(sortTicketsByLatestActivity(res.data));
            } else {
                window.alert("Failed to load support tickets.");
            }
        } catch (err) {
            console.error("Error fetching support tickets:", err);
            window.alert("Something went wrong while fetching tickets.");
        } finally {
            setLoading(false);
        }
    };

    const markAdminRepliesSeen = async () => {
        try {
            const payload = {
                supportID,
                who: "admin",
            };
            const updateRes = await updateSeen(payload);
            if (updateRes.error === false) {
                setResponses(updateRes.data.responses || []);
                fetchTickets();
            } else {
                window.alert("Failed to update seen status.");
            }
        } catch (err) {
            console.error("Error updating seen status:", err);
        } finally {
            setIsSeenStatus(false);
        }
    };

    const handleSelectTicket = (ticket) => {
        setIsCreatingNew(false);
        setSelectedTicket(ticket);
        setSupportID(ticket._id);
        setStatus(ticket.status);
        setResponses(ticket.responses || []);
        setChatInput("");
        setComplaintType("");

        sessionStorage.setItem("selectedTicketId", ticket._id);

        const unseen = getUnseenAdminReplies(ticket.responses || []);
        setIsSeenStatus(unseen > 0);
    };

    const handleCreateNewTicket = () => {
        setIsCreatingNew(true);
        setSelectedTicket(null);
        setSupportID(null);
        setStatus("open");
        setComplaintType("");
        setChatInput("");

        sessionStorage.removeItem("selectedTicketId");

        setResponses([
            {
                who: "admin",
                message: `Hi ${userName || "there"}, how may we help you?`,
                dateTime: new Date(),
            },
        ]);
    };

    const handleSendMessage = async () => {
        const text = chatInput.trim();
        if (!text) {
            window.alert("Please type a message before sending.");
            return;
        }

        // New ticket, first user message -> create complaint
        if (isCreatingNew && responses.length === 1 && !supportID) {
            if (!complaintType) {
                window.alert("Please select a complaint type.");
                return;
            }

            const newMessage = {
                who: "user",
                dateTime: new Date(),
                message: text,
            };

            setResponses((prev) => [...prev, newMessage]);
            setChatInput("");

            try {
                const createData = {
                    title:
                        COMPLAINT_OPTIONS.find((c) => c.value === complaintType)?.label ||
                        "Other Issues",
                    description: text,
                    userName: userName || "partner",
                    role: "partner",
                };

                const update = await UpdateComplaint(createData);
                if (update.error === false) {
                    setSupportID(update.data._id);
                    setStatus(update.data.status);
                    setResponses(update.data.responses || []);
                    setIsCreatingNew(false);

                    sessionStorage.setItem("selectedTicketId", update.data._id);
                    fetchTickets();
                } else {
                    window.alert("Something went wrong, please try again.");
                }
            } catch (err) {
                console.error("Error creating complaint:", err);
                window.alert("Something went wrong, please try again.");
            }
            return;
        }

        // Existing ticket -> addMessage
        if (!supportID) {
            window.alert("No ticket selected. Please create a new complaint.");
            return;
        }

        const newMessage = {
            who: "user",
            dateTime: new Date(),
            message: text,
        };

        setResponses((prev) => [...prev, newMessage]);
        setChatInput("");

        try {
            const messageData = {
                supportID,
                message: newMessage,
            };
            const update = await addMessage(messageData);
            if (update.error === false) {
                setSupportID(update.data._id);
                setStatus(update.data.status);
                setResponses(update.data.responses || []);
                fetchTickets();
            } else {
                window.alert("Something went wrong, please try again.");
            }
        } catch (err) {
            console.error("Error sending message:", err);
            window.alert("Something went wrong while sending the message.");
        }
    };

    const TicketItem = ({ ticket }) => {
        const initials = getInitials(ticket.title || "Ticket");
        const unseenCount = getUnseenAdminReplies(ticket.responses || []);
        const isSelected = selectedTicket?._id === ticket._id;

        const statusColorMap = {
            open: {
                bg: "bg-green-50",
                text: "text-green-700",
                dot: "text-green-600",
            },
            escalated: {
                bg: "bg-indigo-50",
                text: "text-indigo-700",
                dot: "text-indigo-600",
            },
            closed: {
                bg: "bg-red-50",
                text: "text-red-700",
                dot: "text-red-600",
            },
        };

        const st = statusColorMap[ticket.status] || statusColorMap.open;

        const shortDesc =
            (ticket.description || "").length < 60
                ? ticket.description
                : `${ticket.description.slice(0, 60)}...`;

        return (
            <button
                type="button"
                onClick={() => handleSelectTicket(ticket)}
                className={`w-full text-left rounded-xl border transition-colors px-3 py-2 mb-2 flex gap-3 ${isSelected
                    ? "border-[#4951EC] bg-[#EEF0FF]"
                    : "border-gray-200 hover:bg-gray-50"
                    }`}
            >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-orange-700">
                        {initials}
                    </span>
                </div>

                <div className="flex-1 min-w-0 flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-semibold text-gray-900 truncate">
                                {ticket.title || "Untitled"}
                            </span>
                            {unseenCount > 0 && (
                                <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] min-w-[18px] h-[18px] px-1">
                                    {unseenCount}
                                </span>
                            )}
                        </div>
                        <div
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] ${st.bg} ${st.text}`}
                        >
                            <MdCircle className={` ${st.dot}`} size={10} />
                            <span className="capitalize">{ticket.status}</span>
                        </div>
                    </div>
                    <div className="flex items-center justify-between text-[9px] text-gray-500">
                        <span>
                            <span className="text-blue-500 mr-1">#</span>
                            {ticket.ticketNumber || "—"}
                        </span>
                        <span >
                            {ticket.updatedAt
                                ? moment(ticket.updatedAt).format("DD MMM, YYYY hh:mm A")
                                : ""}
                        </span>
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-2">{shortDesc}</p>
                </div>
            </button>
        );
    };

    const ChatBubble = ({ msg }) => {
        const isAdmin = msg.who === "admin";
        const align = isAdmin ? "justify-start" : "justify-end";
        const bubbleColor = isAdmin
            ? "bg-[#EEF0FF] text-gray-900"
            : "bg-[#E9FCE7] text-gray-900";

        return (
            <div className={`flex ${align} gap-1.5`}>
                {isAdmin && (
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#EEF0FF] flex items-center justify-center text-[9px] font-semibold text-[#4951EC]">
                        CS
                    </div>
                )}
                <div
                    className={`max-w-[75%] rounded-[12px] px-2 py-1 ${bubbleColor} flex flex-col`}
                >
                    {msg.title && (
                        <div
                            className={`mb-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full self-start ${isAdmin ? "bg-[#4951EC] text-white" : "bg-green-600 text-white"
                                }`}
                        >
                            {msg.title}
                        </div>
                    )}
                    <div className="text-[13px] leading-snug whitespace-pre-wrap break-words">
                        {msg.message}
                    </div>
                    <div className="text-[9px] text-gray-500 mt-0.5 self-end">
                        {msg.dateTime
                            ? moment(msg.dateTime).format("MMM DD, YYYY hh:mm A")
                            : ""}
                    </div>
                </div>
                {!isAdmin && (
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[9px] text-gray-500">
                        U
                    </div>
                )}
            </div>
        );
    };

    const showChatInput =
        (selectedTicket && status !== "closed") || (isCreatingNew && !supportID);
    const showClosedMessage = selectedTicket && status === "closed";

    if (loading) return <Loader />;

    return (
        <div className="font-poppins overflow-x-auto bg-white rounded-md w-auto md:w-auto lg:w-auto shadow-lg p-2 h-[86vh] m-2 flex flex-col gap-3">
            <div className="bg-white rounded-2xl shadow-sm p-4 border border-[#9EA3FF] flex-1 min-h-0 flex flex-col">
                {/* Header */}
                <div className="flex-shrink-0 flex items-center justify-between mb-2">
                    <h2 className="text-[15px] font-semibold text-gray-900">
                        Help &amp; Support
                    </h2>
                    <button
                        type="button"
                        onClick={handleCreateNewTicket}
                        className="flex items-center gap-2 bg-[#4951EC] text-[13px] hover:bg-[#3d3acc] text-white px-3 py-1.5 rounded-[6px] transition-colors"
                    >
                        <span className="text-lg leading-none">+</span>
                        <span>Create Complaint</span>
                    </button>
                </div>

                {/* Main content */}
                <div className="flex flex-1 min-h-0 gap-4 overflow-hidden mt-2">
                    {/* LEFT: ticket list */}
                    <div className="w-1/3 flex flex-col border border-gray-200 rounded-2xl bg-white min-h-0">
                        <div className="flex-shrink-0 px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                            <span className="text-[13px] font-semibold text-gray-800">
                                Your Tickets
                            </span>
                            <span className="text-[11px] text-gray-500">
                                Total:{" "}
                                <span className="font-semibold text-gray-700">
                                    {tickets.length}
                                </span>
                            </span>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto p-3">
                            {tickets.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-xs text-gray-500 gap-2">
                                    <p>No tickets found.</p>
                                    <p>Click &quot;Create Complaint&quot; to raise a new one.</p>
                                </div>
                            ) : (
                                tickets.map((t) => <TicketItem key={t._id} ticket={t} />)
                            )}
                        </div>
                    </div>

                    {/* RIGHT: chat area */}
                    <div className="flex-1 flex flex-col border border-gray-200 rounded-2xl bg-white min-h-0">
                        {/* Chat Header */}
                        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                            {isCreatingNew && !supportID ? (
                                <div className="flex flex-col gap-1">
                                    <span className="text-sm font-semibold text-gray-900">
                                        New Complaint
                                    </span>
                                    <span className="text-[11px] text-gray-500">
                                        Select complaint type and describe your issue.
                                    </span>
                                </div>
                            ) : selectedTicket ? (
                                <>
                                    <div className="flex flex-col">
                                        <span className="text-sm font-semibold text-gray-900 truncate">
                                            {selectedTicket.title || "Ticket"}
                                        </span>
                                        <span className="text-[11px] text-gray-500">
                                            #{selectedTicket.ticketNumber || "—"}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                        <span
                                            className={`capitalize px-2 py-0.5 rounded-full ${status === "closed"
                                                ? "bg-red-50 text-red-700"
                                                : status === "escalated"
                                                    ? "bg-indigo-50 text-indigo-700"
                                                    : "bg-green-50 text-green-700"
                                                }`}
                                        >
                                            {status || "unknown"}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <span className="text-sm text-gray-500">
                                    Select a ticket on the left or create a new complaint.
                                </span>
                            )}
                        </div>

                        {/* Chat messages */}
                        <div
                            ref={messagesContainerRef}
                            className="flex-1 min-h-0 overflow-y-auto bg-gray-50 px-4 py-2 space-y-1.5"
                        >
                            {responses.length ? (
                                responses.map((msg, i) => (
                                    <ChatBubble key={`${msg._id || i}`} msg={msg} />
                                ))
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-xs text-gray-400 gap-2">
                                    <p>No messages yet.</p>
                                    <p>
                                        {isCreatingNew
                                            ? "Type your message below to start."
                                            : "Select a ticket or create a new complaint."}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Chat input */}
                        {showChatInput && (
                            <div className="flex-shrink-0 border-t border-gray-200 bg-white px-3 py-2">
                                {isCreatingNew && !supportID && (
                                    <div className="mb-2 px-1 py-2 rounded-md bg-gray-50 border border-gray-100">
                                        <p className="text-[11px] font-medium text-gray-600 mb-2">
                                            Please select your complaint type
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {COMPLAINT_OPTIONS.map((opt) => {
                                                const active = complaintType === opt.value;
                                                return (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        onClick={() => setComplaintType(opt.value)}
                                                        className={`px-3 py-1 rounded-full text-[11px] border transition-colors ${active
                                                            ? "bg-[#4951EC] border-[#4951EC] text-white"
                                                            : "bg-white border-[#4951EC] text-gray-700 hover:bg-gray-50"
                                                            }`}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                <div className="flex items-center gap-2">
                                    <textarea
                                        className="flex-1 resize-none border border-gray-300 rounded-lg px-2 py-1 text-[12px] focus:ring-1 focus:ring-[#4951EC] focus:border-[#4951EC] outline-none"
                                        placeholder="Type your message..."
                                        value={chatInput}
                                        rows={1}
                                        style={{ maxHeight: "80px", minHeight: "32px" }}
                                        onChange={(e) => setChatInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendMessage();
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSendMessage}
                                        className="flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full bg-[#4951EC] text-white hover:bg-[#3d3acc] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                                        disabled={!chatInput.trim()}
                                    >
                                        <FiSend size={16} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Closed ticket message */}
                        {showClosedMessage && (
                            <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-2 text-[11px] text-gray-500">
                                This ticket is{" "}
                                <span className="font-semibold lowercase">{status}</span>. You
                                cannot send new messages. Create a new complaint if you need
                                further assistance.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HelpAdmin;